"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useActionState, type ReactNode } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useSetAtom } from "jotai";
import { Check, X } from "lucide-react";
import { Typography } from "@repo/ui/aops-components/typography";
import { cartHasCheckoutWarningAtom } from "@/app/atoms/cartHasCheckoutWarning";
import { cartItemCountAtom } from "@/app/atoms/cartItemCount";
import { addToCartAction, type AddToCartActionResult } from "@/app/actions/add-to-cart";
import { useDiscoveryUser } from "@/app/atoms/useDiscoveryUser";
import { useParentStudents } from "@/app/atoms/useParentStudents";
import { discoveryRoutePath } from "@/lib/discoveryRoutePath";
import { CourseOfferingClassInstances } from "@repo/utils/types/CourseOfferingTypes";
import { buildAddToCartLoginHref } from "./buildAddToCartLoginHref";
import styles from "./AddToCartButton.module.css";
type AddToCartButtonProps = {
  classInstance: CourseOfferingClassInstances;
};

/**
 * Form adapter for `useActionState`: reads student + class IDs from FormData
 * and returns the add-to-cart result so the UI can show success/failure.
 */
async function submitAddToCart(_previousState: AddToCartActionResult | null, formData: FormData): Promise<AddToCartActionResult> {
  const studentUserId = String(formData.get("studentUserId") ?? "");
  const classInstanceId = String(formData.get("classInstanceId") ?? "");
  return await addToCartAction(studentUserId, classInstanceId);
}
function GuestAddToCartLink(t0) {
  const $ = _c(7);
  const {
    classInstanceId,
    noSeatsAvailable,
    pathname
  } = t0;
  const t1 = useSearchParams();
  let t2;
  if ($[0] !== classInstanceId || $[1] !== pathname || $[2] !== t1) {
    const query = t1.toString();
    t2 = buildAddToCartLoginHref(discoveryRoutePath("/login"), pathname, query, classInstanceId);
    $[0] = classInstanceId;
    $[1] = pathname;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const loginHref = t2;
  if (noSeatsAvailable) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <button type="button" className={styles.addToCartButton} disabled={true} aria-label="Add to cart unavailable, no seats remaining">Add to Cart</button>;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t3;
  if ($[5] !== loginHref) {
    t3 = <a href={loginHref} className={styles.addToCartButton} aria-label="Add to cart, sign in required">Add to Cart</a>;
    $[5] = loginHref;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function GuestAddToCartButton(t0) {
  const $ = _c(13);
  const {
    classInstance
  } = t0;
  const noSeatsAvailable = classInstance.seatsAvailable === 0;
  const pathname = usePathname() || "/";
  let t1;
  if ($[0] !== classInstance.id || $[1] !== pathname) {
    t1 = buildAddToCartLoginHref(discoveryRoutePath("/login"), pathname, "", classInstance.id);
    $[0] = classInstance.id;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const fallbackHref = t1;
  let t2;
  if ($[3] !== fallbackHref || $[4] !== noSeatsAvailable) {
    t2 = noSeatsAvailable ? <button type="button" className={styles.addToCartButton} disabled={true} aria-label="Add to cart unavailable, no seats remaining">Add to Cart</button> : <a href={fallbackHref} className={styles.addToCartButton} aria-label="Add to cart, sign in required">Add to Cart</a>;
    $[3] = fallbackHref;
    $[4] = noSeatsAvailable;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== classInstance.id || $[7] !== noSeatsAvailable || $[8] !== pathname) {
    t3 = <GuestAddToCartLink classInstanceId={classInstance.id} noSeatsAvailable={noSeatsAvailable} pathname={pathname} />;
    $[6] = classInstance.id;
    $[7] = noSeatsAvailable;
    $[8] = pathname;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <div className={styles.addToCart}><Suspense fallback={t2}>{t3}</Suspense></div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function AuthenticatedAddToCartButton(t0) {
  const $ = _c(30);
  const {
    classInstance
  } = t0;
  const students = useParentStudents();
  const setCartItemCount = useSetAtom(cartItemCountAtom);
  const setCartHasCheckoutWarning = useSetAtom(cartHasCheckoutWarningAtom);
  let t1;
  if ($[0] !== setCartHasCheckoutWarning || $[1] !== setCartItemCount) {
    t1 = async (previousState, formData) => {
      const result = await submitAddToCart(previousState, formData);
      if (result.success) {
        if (result.itemCount > 0) {
          setCartItemCount(result.itemCount);
          setCartHasCheckoutWarning(result.hasCheckoutWarning);
        }
        if (result.shopifyOidcLoginUrl) {
          window.location.assign(result.shopifyOidcLoginUrl);
        }
      }
      return result;
    };
    $[0] = setCartHasCheckoutWarning;
    $[1] = setCartItemCount;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [state, formAction, isPending] = useActionState(t1, null);
  if (students.length === 0) {
    return null;
  }
  const noSeatsAvailable = classInstance.seatsAvailable === 0;
  const isSuccess = state?.success === true;
  const isFailure = state?.success === false;
  let statusIcon = undefined;
  let buttonAriaLabel = "Add to cart";
  if (noSeatsAvailable) {
    buttonAriaLabel = "Add to cart unavailable, no seats remaining";
  } else {
    if (isPending) {
      buttonAriaLabel = "Adding to cart";
    } else {
      if (isSuccess) {
        buttonAriaLabel = "Added to cart";
        let t2;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <Check size={16} strokeWidth={2.5} aria-hidden={true} />;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        statusIcon = t2;
      } else {
        if (isFailure) {
          buttonAriaLabel = "Failed to add to cart";
          let t2;
          if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
            t2 = <X size={16} strokeWidth={2.5} aria-hidden={true} />;
            $[4] = t2;
          } else {
            t2 = $[4];
          }
          statusIcon = t2;
        }
      }
    }
  }
  let buttonText = "Add to Cart";
  if (isPending) {
    buttonText = "Adding...";
  } else {
    if (isSuccess) {
      buttonText = "Added to Cart";
    }
  }
  let t2;
  if ($[5] !== classInstance.id) {
    t2 = <input type="hidden" name="classInstanceId" value={classInstance.id} />;
    $[5] = classInstance.id;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== students) {
    t3 = students.map(_temp);
    $[7] = students;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== noSeatsAvailable || $[10] !== t3) {
    t4 = <select name="studentUserId" disabled={noSeatsAvailable}>{t3}</select>;
    $[9] = noSeatsAvailable;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = noSeatsAvailable || isPending || isSuccess;
  let t6;
  if ($[12] !== buttonAriaLabel || $[13] !== buttonText || $[14] !== statusIcon || $[15] !== t5) {
    t6 = <button type="submit" className={styles.addToCartButton} disabled={t5} aria-label={buttonAriaLabel}>{buttonText} {statusIcon}</button>;
    $[12] = buttonAriaLabel;
    $[13] = buttonText;
    $[14] = statusIcon;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t2 || $[18] !== t4 || $[19] !== t6) {
    t7 = <form action={formAction} className={styles.addToCartForm}>{t2}{t4}{t6}</form>;
    $[17] = t2;
    $[18] = t4;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== isFailure || $[22] !== isPending || $[23] !== state) {
    t8 = !isPending && isFailure && <Typography variant="bodySmall" className={styles.errorMessage}>{state.errorMessage}</Typography>;
    $[21] = isFailure;
    $[22] = isPending;
    $[23] = state;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t8) {
    t9 = <div className={styles.addToCartStatus}>{t8}</div>;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t7 || $[28] !== t9) {
    t10 = <div className={styles.addToCart}>{t7}{t9}</div>;
    $[27] = t7;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}

/**
 * Add to cart button for a course page schedule.
 * Guests see a login link; logged-in parents with students see the enroll form.
 */
function _temp(student) {
  return <option key={student.id} value={student.id}>{student.displayName}</option>;
}
export function AddToCartButton(t0) {
  const $ = _c(4);
  const {
    classInstance
  } = t0;
  const user = useDiscoveryUser();
  if (!user) {
    let t1;
    if ($[0] !== classInstance) {
      t1 = <GuestAddToCartButton classInstance={classInstance} />;
      $[0] = classInstance;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== classInstance) {
    t1 = <AuthenticatedAddToCartButton classInstance={classInstance} />;
    $[2] = classInstance;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}